import {DatePipe, NgForOf, NgOptimizedImage} from '@angular/common';
import {Component, OnInit, TemplateRef, viewChild} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatButton} from '@angular/material/button';
import {MatChip} from '@angular/material/chips';
import {MatDialog, MatDialogContent} from '@angular/material/dialog';
import {
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle
} from '@angular/material/expansion';
import {MatFormField} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {MatMenu, MatMenuItem, MatMenuTrigger} from '@angular/material/menu';
import {MatStep, MatStepLabel, MatStepper, MatStepperNext, MatStepperPrevious} from '@angular/material/stepper';
import {MatTab, MatTabGroup} from '@angular/material/tabs';
import {ActivatedRoute, Router} from '@angular/router';
import {NgbModalRef} from '@ng-bootstrap/ng-bootstrap/modal/modal-ref';
import {LucideAngularModule} from 'lucide-angular';
import {ComponentsModule} from '../../../components/components.module';
import {
    NavigationTableRequestModel,
    NavigationTableResponseItemModel,
    PaginationModel
} from '../../../models/file-table.model';
import {ProductModel, ProductPhotoModel, PublicProductModel} from '../../../models/products.model';
import {HelpersService} from '../../../services/helpers.service';
import {ProductionService} from '../../../services/production.service';
import {MemberService} from '../../../services/member.service';
import {ModalService} from '../../../services/modal/modal.service';
import {AlertService} from '../../../services/notify/alert.service';
import {DocumentsServiceService} from '../../documents/documents-service.service';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';

@Component({
    selector: 'app-detail-card',
    standalone: true,
    imports: [
        ComponentsModule,
        NgForOf,
        LucideAngularModule,
        MatTab,
        MatTabGroup,
        MatAccordion,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        NgOptimizedImage,
        MatButton,
        MatDialogContent,
        MatStepper,
        MatStep,
        MatFormField,
        ReactiveFormsModule,
        MatStepperNext,
        MatInput,
        MatStepLabel,
        MatStepperPrevious,
        MatChip,
        MatMenu,
        MatMenuItem,
        MatMenuTrigger,
        DatePipe,
    ],
    templateUrl: './detail-card.component.html',
    styleUrl: './detail-card.component.scss'
})

export class DetailCardComponent implements OnInit {
    puid: string;
    card: ProductModel;
    publicCards: PublicProductModel[];

    previews: any[] = [];

    selectedProductCardPhotos: File[] = [];

    collectionId: number = null;
    documentKey: string = null;

    activeBigPhoto: ProductPhotoModel;

    photoCheckForm: FormGroup;

    readonly menuTrigger = viewChild.required(MatMenuTrigger);

    get photosFormArray() {
        return this.photoCheckForm.controls.photos as FormArray;
    }

    secondFormGroup = this.fb.group({
        secondCtrl: ['', Validators.required],
    });

    constructor(
        protected controller: DocumentsServiceService,
        private activeRouter: ActivatedRoute,
        private productionService: ProductionService,
        private alertService: AlertService,
        private memberService: MemberService,
        private sanitizer: DomSanitizer,
        private dialog: MatDialog,
        private fb: FormBuilder,
        public helpers: HelpersService,
        public modalService: ModalService,
        public router: Router,
    ) {
        this.photoCheckForm = this.fb.group({
            photos: new FormArray([])
        });
    }

    ngOnInit() {
        this.activeRouter.params.subscribe(params => {
            this.puid = params.puid;
            this.getCard(this.puid);
        });
    }

    getCard(puid: string): void {
        this.productionService.getProductCard(puid).subscribe({
            next: res => {
                this.card = res.product;
                this.getPhotos();

                this.memberService.getMemberGuid(this.card.product_card.creator_guid).subscribe({
                    next: res => {
                        this.card.product_card.author = res;
                    }
                });
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
        this.productionService.getAllPublicCards(puid).subscribe({
            next: res => {
                this.publicCards = res.product_public;
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    getPhotos(): void {
        if (this.card.photo_first) {
            this.productionService.getPhotoInProductCard(this.puid, this.card.photo_first.ukey).subscribe({
                next: res => {
                    this.card.photo_first.originImage = this.convertBlobToImage(res);
                    this.activeBigPhoto = this.card.photo_first;
                },
                error: err => {
                    this.alertService.setAlert(err.message.info, 'blue');
                }
            });
        }
        this.card.photos.forEach(image => {
            this.productionService.getPhotoInProductCard(this.puid, image.ukey).subscribe({
                next: res => {
                    image.originImage = this.convertBlobToImage(res);
                },
                error: err => {
                    this.alertService.setAlert(err.message.info, 'blue');
                }
            });
        });
    }

    private convertBlobToImage(blob: Blob): SafeResourceUrl {
        return this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(blob));
    }

    nextPhoto(): void {
        const currentImageIndex = this.card.photos.indexOf(this.card.photos.find(x => x.ukey === this.activeBigPhoto.ukey));
        if (currentImageIndex + 1 === this.card.photos.length) {
            this.activeBigPhoto = this.card.photos[0];
        } else {
            this.activeBigPhoto = this.card.photos[currentImageIndex + 1];
        }
    }

    prevPhoto(): void {
        const currentImageIndex = this.card.photos.indexOf(this.card.photos.find(x => x.ukey === this.activeBigPhoto.ukey));
        if (currentImageIndex === 0) {
            this.activeBigPhoto = this.card.photos.at(-1);
        } else {
            this.activeBigPhoto = this.card.photos[currentImageIndex - 1];
        }
    }

    createPublicProductCard(): void {
        this.productionService.createPublicProductCard(this.puid).subscribe({
            next: () => {
                this.getCard(this.puid);
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    viewImageUpload(data: Event): void {
        this.selectedProductCardPhotos = [];
        const target = data.target as HTMLInputElement;
        const files = target.files;

        if (files) {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                this.selectedProductCardPhotos.push(file);
                const reader = new FileReader();

                reader.onload = (e) => {
                    this.previews.push(e.target.result);
                };

                reader.readAsDataURL(file);
            }
        }
    }

    addNewPhotos(modal: NgbModalRef): void {
        this.selectedProductCardPhotos.forEach(file => {
            this.productionService.addPhotoToProductCard(this.puid, {photo: file}).subscribe({
                next: () => {
                    this.previews = [];

                    if (modal) {
                        modal.close();
                    }
                    this.getCard(this.puid);
                }, error: error => {
                    this.alertService.setAlert(error, 'blue');
                },
            });
        });
    }

    removeImage(files: HTMLInputElement, i: number): void {
        this.previews.splice(i, 1);

        let fileBuffer = Array.from(files.files);
        fileBuffer.splice(i, 1);

        const dT = new ClipboardEvent('').clipboardData || new DataTransfer();

        for (let file of fileBuffer) {
            dT.items.add(file);
        }
        files.files = dT.files;
        console.log(files.files, i);
    }

    selectCollection(collection: NavigationTableResponseItemModel): void {
        if (collection) {
            this.collectionId = collection.id;
        }
    }

    addCollection(modal: NgbModalRef): void {
        this.productionService.addCollectionToProductCard(this.puid, this.collectionId).subscribe({
            next: res => {
                modal.close();
                this.alertService.setAlert(res.message, 'green');
                this.getCard(this.puid);
            }, error: () => {
                modal.close();
            },
        });
    }

    onEventFind(event: NavigationTableRequestModel | PaginationModel): void {
        this.controller.loadNav(this.controller.prepare(event));
    }

    onEventClick(event: NavigationTableResponseItemModel, documentModal: TemplateRef<any>): void {
        this.documentKey = event.key;
        this.modalService.openTemplateModal(documentModal, 'xl');
    }

    openCollectionAccordion(collectionId: any): void {
        const request: NavigationTableRequestModel = new NavigationTableRequestModel();
        request.filter = 'collection';
        request.id = collectionId;
        request.limit = 5;
        this.controller.loadNav(this.controller.prepare(request));
    }

    removeCollection(collectionId: number): void {
        this.productionService.removeCollectionFromProductCard(collectionId, this.puid).subscribe({
            next: () => {
                this.getCard(this.puid);
            }, error: err => {
                this.alertService.setAlert(err.message, 'blue');
            }
        });
    }

    submitImages() {
        const defaultPhotos = structuredClone(this.card.photos);

        const selectedOrderIds = this.photoCheckForm.value.photos
            .map((v, i) => v ? defaultPhotos[i].ukey : null)
            .filter(v => v !== null);
        console.log(selectedOrderIds.toString());
    }

    openClonerModal(modalTemplate: TemplateRef<any>): void {
        this.card.photos.forEach(() => this.photosFormArray.push(new FormControl(true)));

        this.submitImages();
        const dialogRef = this.dialog.open(modalTemplate, {minWidth: 1000});

        dialogRef.afterClosed().subscribe(result => {
            this.photoCheckForm = this.fb.group({
                photos: new FormArray([])
            });
            console.log(`Dialog result: ${result}`);
        });
    }

    editCard(key: string, value: string): void {
        const data = {
            title: this.card.product_card.title,
            description: this.card.product_card.description,
        };

        data[key] = value;

        this.productionService.editPrivateProductCard(this.card.puid, data).subscribe({
            next: res => {
                this.getCard(this.puid);
                console.log(res);
            }, error: error => {
                console.log(error);
            }
        });
    }
}
